import { useState, useEffect } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_PB, s_GB, s_GC } from '../../shared/styles';
import Avatar from '../../components/ui/Avatar';
import SocialStatsCard from '../social/SocialStatsCard';
import ConnectionStrength from '../social/ConnectionStrength';
import ProfileBadges from '../social/ProfileBadges';
import { fetchUserProfile, setRestUntil } from '../../shared/dbService';
import { timeAgo } from '../../shared/utils';
import PhotoLightbox from '../../components/ui/PhotoLightbox';

const BASE = process.env.REACT_APP_API_URL || '/api';

const MOOD_MAP = {
  great:   { emoji: '😄', label: 'Geweldig', labelEN: 'Great',   color: '#4AC87A', score: 5 },
  good:    { emoji: '🙂', label: 'Goed',     labelEN: 'Good',    color: '#C8A84B', score: 4 },
  neutral: { emoji: '😐', label: 'Neutraal', labelEN: 'Neutral', color: '#8899BB', score: 3 },
  low:     { emoji: '😔', label: 'Matig',    labelEN: 'Low',     color: '#D4873A', score: 2 },
  bad:     { emoji: '😞', label: 'Slecht',   labelEN: 'Bad',     color: '#C05050', score: 1 },
};

const PROFILE_TABS = [
  { id: 'about', nl: 'Over',      en: 'About' },
  { id: 'posts', nl: 'Berichten', en: 'Posts' },
  { id: 'stats', nl: 'Stats',     en: 'Stats' },
];

function CompetitionHonours({ userId, lang }) {
  const nl = lang === 'nl';
  const [honours, setHonours] = useState(null);

  useEffect(() => {
    if (!userId) return;
    fetch(`${BASE}/users/${userId}/competition-wins`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setHonours(d); })
      .catch(() => {});
  }, [userId]);

  if (!honours || (honours.wins === 0 && honours.podiums === 0 && honours.participations === 0)) return null;

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 8 }}>
        ⚔️ {nl ? 'COMPETITIE' : 'COMPETITION'}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {honours.wins > 0 && (
          <div style={{ flex: 1, textAlign: 'center', background: '#C8A84B12', border: '1px solid #C8A84B33', borderRadius: 14, padding: '12px 6px' }}>
            <div style={{ fontSize: 24 }}>🥇</div>
            <div style={{ fontSize: 20, color: '#C8A84B', fontWeight: 700, lineHeight: 1.1 }}>{honours.wins}</div>
            <div style={{ fontSize: 9, color: C.silver, marginTop: 3 }}>{nl ? 'Gewonnen' : 'Win(s)'}</div>
          </div>
        )}
        {honours.podiums > 0 && (
          <div style={{ flex: 1, textAlign: 'center', background: '#88888812', border: '1px solid #88888833', borderRadius: 14, padding: '12px 6px' }}>
            <div style={{ fontSize: 24 }}>🏅</div>
            <div style={{ fontSize: 20, color: '#aaaaaa', fontWeight: 700, lineHeight: 1.1 }}>{honours.podiums}</div>
            <div style={{ fontSize: 9, color: C.silver, marginTop: 3 }}>{nl ? 'Podium' : 'Podium(s)'}</div>
          </div>
        )}
        {honours.participations > 0 && (
          <div style={{ flex: 1, textAlign: 'center', background: `${_T.accent}12`, border: `1px solid ${_T.accent}33`, borderRadius: 14, padding: '12px 6px' }}>
            <div style={{ fontSize: 24 }}>⚔️</div>
            <div style={{ fontSize: 20, color: _T.accent, fontWeight: 700, lineHeight: 1.1 }}>{honours.participations}</div>
            <div style={{ fontSize: 9, color: C.silver, marginTop: 3 }}>{nl ? 'Deelname' : 'Competed'}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function RestPicker({ userId, lang, currentRestUntil, onChanged }) {
  const nl = lang === 'nl';
  const [saving, setSaving] = useState(false);
  const isResting = currentRestUntil && new Date(currentRestUntil) > new Date();

  const setRest = async (minutes) => {
    setSaving(true);
    const until = await setRestUntil(minutes);
    setSaving(false);
    onChanged(until);
  };

  if (isResting) {
    const until = new Date(currentRestUntil);
    const timeStr = until.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return (
      <div style={{ background: '#1a2a3a', border: '1px solid #2A9E8244', borderRadius: 14, padding: '12px 14px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: '#2A9E82', fontWeight: 700 }}>🌙 {nl ? 'Rustmodus actief' : 'Rest mode active'}</div>
            <div style={{ fontSize: 10, color: C.silver, marginTop: 2 }}>{nl ? `Tot ${timeStr}` : `Until ${timeStr}`}</div>
          </div>
          <button onClick={() => setRest(0)} disabled={saving}
            style={{ background: 'transparent', border: '1px solid #C0505044', color: '#C05050', borderRadius: 10, padding: '6px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
            {saving ? '...' : (nl ? 'Einde rust' : 'End rest')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...s_GC({ marginBottom: 14 }), padding: '12px 14px' }}>
      <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 10 }}>
        🌙 {nl ? 'RUSTMODUS INSTELLEN' : 'SET REST MODE'}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[
          { mins: 30,  label: '30m' },
          { mins: 60,  label: '1u' },
          { mins: 120, label: '2u' },
          { mins: 240, label: '4u' },
        ].map(({ mins, label }) => (
          <button key={mins} onClick={() => setRest(mins)} disabled={saving}
            style={{ background: `${_T.accent}12`, border: `1px solid ${_T.accent}33`, color: _T.accent, borderRadius: 20, padding: '6px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 700, opacity: saving ? 0.5 : 1 }}>
            {label}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 10, color: `${C.silver}88`, marginTop: 8 }}>
        {nl ? 'Vrienden zien dat je rust en kunnen je niet storen.' : 'Friends see you\'re resting and won\'t disturb you.'}
      </div>
    </div>
  );
}

function ProfilePage({ user, lang, currentUserId, friends, friendRequests, onFriendRequest, onBack, onOpenChat, onAcceptFriend, onDeclineFriend, myPosts, allUsers, onPlayTogether, userBusiness, onViewBizPage }) {
  const nl = lang === 'nl';
  const isMe = user.id === currentUserId;
  const isFriend = (friends || []).includes(user.id);
  const hasSentRequest = (friendRequests || []).some(r => r.to === user.id && r.from === currentUserId && r.status === 'pending');
  const hasIncoming = (friendRequests || []).some(r => r.from === user.id && r.to === currentUserId && r.status === 'pending');

  const [activeTab, setActiveTab] = useState('about');
  const [fetchedPosts, setFetchedPosts] = useState(null);
  const [bannerImg, setBannerImg] = useState(user.bannerImg || null);
  const [lastMood, setLastMood] = useState(user.lastMood || null);
  const [recentSessions, setRecentSessions] = useState(null);
  const [restUntil, setRestUntilState] = useState(user.restUntil || null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [bizInfo, setBizInfo] = useState(userBusiness || null);

  useEffect(() => {
    // Always fetch posts with images from the posts table (works for own and other profiles)
    const targetId = isMe ? currentUserId : user.id;
    fetch(`${BASE}/posts/${targetId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (Array.isArray(data) && data.length > 0) setFetchedPosts(data); })
      .catch(() => {});

    if (isMe) {
      try {
        const b = localStorage.getItem(`sl_banner_img_${currentUserId}`) || localStorage.getItem('sl_banner_img');
        if (b) setBannerImg(b);
        const moodLog = JSON.parse(localStorage.getItem('sl_mood_log') || '[]');
        if (moodLog.length > 0) setLastMood(moodLog[moodLog.length - 1].mood || moodLog[moodLog.length - 1].id);
        const exHist = JSON.parse(localStorage.getItem('sl_ex_history') || '[]');
        const weekAgo = Date.now() - 7 * 86400000;
        setRecentSessions(exHist.filter(e => e.date > weekAgo).length);
      } catch {}
      return;
    }
    fetchUserProfile(user.id).then(data => {
      if (!data) return;
      if (data.sl_banner_img) setBannerImg(data.sl_banner_img);
      const ml = data.sl_mood_log || [];
      if (ml.length > 0) {
        const last = ml[ml.length - 1];
        setLastMood(last.mood || last.id || null);
      }
      const ex = data.sl_ex_history || [];
      const weekAgo = Date.now() - 7 * 86400000;
      setRecentSessions(ex.filter(e => (e.date || 0) > weekAgo).length);
    });
  }, [user.id, isMe, currentUserId]); // eslint-disable-line

  useEffect(() => {
    if (userBusiness) { setBizInfo(userBusiness); return; }
    fetch(`${BASE}/businesses/by-user/${user.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.id) setBizInfo(d); })
      .catch(() => {});
  }, [user.id, userBusiness]); // eslint-disable-line

  const posts = fetchedPosts ?? (isMe ? (myPosts || []) : (user.posts ?? []));
  const moodData = lastMood ? (MOOD_MAP[lastMood] || null) : null;
  const isResting = restUntil && new Date(restUntil) > new Date();
  const accentCol = user.color || _T.accent;

  const tabBar = (
    <div style={{ display: 'flex', borderBottom: `1px solid ${_T.cardBorderBot}`, marginBottom: 20, marginTop: 4 }}>
      {PROFILE_TABS.map(t => {
        const active = activeTab === t.id;
        return (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ flex: 1, background: 'none', border: 'none', borderBottom: `2px solid ${active ? accentCol : 'transparent'}`, cursor: 'pointer', padding: '12px 4px', color: active ? accentCol : C.silver, fontSize: 12, fontWeight: active ? 700 : 400, transition: 'color 0.15s, border-color 0.15s' }}>
            {nl ? t.nl : t.en}
          </button>
        );
      })}
    </div>
  );

  const aboutTab = (
    <>
      {/* Business badge */}
      {bizInfo && bizInfo.id && (
        <div style={{ background: `${_T.accent}0d`, border: `1px solid ${_T.accent}2a`, borderRadius: 16, padding: '14px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {bizInfo.logo_url
              ? <img src={bizInfo.logo_url} alt="" style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', flexShrink: 0, border: `1px solid ${_T.accent}33` }} />
              : <div style={{ width: 40, height: 40, borderRadius: 10, background: `${_T.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🏢</div>
            }
            <div>
              <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 2, fontWeight: 700, marginBottom: 2 }}>
                {isMe ? (nl ? 'MIJN BEDRIJF' : 'MY BUSINESS') : (nl ? 'EIGENAAR VAN' : 'OWNER OF')}
              </div>
              <div style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>{bizInfo.name}</div>
              {bizInfo.industry && <div style={{ color: C.silver, fontSize: 11, marginTop: 1 }}>{bizInfo.industry}</div>}
            </div>
          </div>
          {onViewBizPage && (
            <button onClick={() => onViewBizPage(bizInfo.id)}
              style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 10, color: '#0a1220', fontSize: 11, fontWeight: 700, padding: '9px 14px', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
              {nl ? 'Bekijk pagina' : 'View page'}
            </button>
          )}
        </div>
      )}

      {/* Intention */}
      {user.intention && (
        <div style={{ position: 'relative', overflow: 'hidden', background: `${accentCol}0a`, border: `1px solid ${accentCol}22`, borderRadius: 14, padding: '12px 16px 12px 20px', marginBottom: 14 }}>
          <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: `linear-gradient(180deg,${_T.grad1},${_T.grad2})` }} />
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 4 }}>{nl ? 'WEEKINTENTIE' : 'WEEKLY INTENTION'}</div>
          <div style={{ color: C.silver2, fontSize: 13, fontStyle: 'italic', lineHeight: 1.6 }}>"{user.intention}"</div>
        </div>
      )}

      {/* Own profile: rest picker */}
      {isMe && (
        <RestPicker userId={currentUserId} lang={lang} currentRestUntil={restUntil} onChanged={v => setRestUntilState(v)} />
      )}

      {/* Vitality strip */}
      {(moodData || recentSessions !== null) && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {moodData && (
            <div style={{ flex: 1, background: `${moodData.color}10`, border: `1px solid ${moodData.color}33`, borderRadius: 14, padding: '14px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 4 }}>{moodData.emoji}</div>
              <div style={{ fontSize: 12, color: moodData.color, fontWeight: 700 }}>{nl ? moodData.label : moodData.labelEN}</div>
              <div style={{ fontSize: 9, color: C.silver, marginTop: 2 }}>{nl ? 'Laatste stemming' : 'Last mood'}</div>
            </div>
          )}
          {recentSessions !== null && (
            <div style={{ flex: 1, background: `${_T.accent}10`, border: `1px solid ${_T.accent}33`, borderRadius: 14, padding: '14px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 4 }}>{recentSessions >= 5 ? '🔥' : recentSessions >= 2 ? '💪' : '🌱'}</div>
              <div style={{ fontSize: 18, color: _T.accent, fontWeight: 700, lineHeight: 1 }}>{recentSessions}</div>
              <div style={{ fontSize: 9, color: C.silver, marginTop: 2 }}>{nl ? 'Sessies (7 dagen)' : 'Sessions (7 days)'}</div>
            </div>
          )}
        </div>
      )}

      {/* Connection strength (friends only, not own profile) */}
      {!isMe && isFriend && (
        <div style={{ ...s_GC({ padding: '14px 16px', marginBottom: 14 }), display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 6 }}>{nl ? 'JULLIE CONNECTIE' : 'YOUR CONNECTION'}</div>
            <ConnectionStrength currentUserId={currentUserId} targetUserId={user.id} lang={lang} mode="bar" showLabel={true} />
          </div>
        </div>
      )}

      {(!user.intention && !moodData && recentSessions === null && !bizInfo) && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: C.silver, fontSize: 13 }}>
          {nl ? 'Nog niets gedeeld.' : 'Nothing shared yet.'}
        </div>
      )}
    </>
  );

  const postsTab = (
    <>
      {posts.length === 0 && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 32 }), color: C.silver, fontSize: 13 }}>
          {nl ? 'Nog geen berichten.' : 'No posts yet.'}
        </div>
      )}
      {posts.map((p, i) => (
        <div key={p.id || i} style={{ background: _T.bg3, borderRadius: 16, padding: 14, border: `1px solid ${_T.cardBorderBot}`, marginBottom: 10, borderLeft: `3px solid ${accentCol}33` }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: (p.img || p.text) ? 10 : 0 }}>
            <Avatar user={user} size={34} profileImage={user.picture || null} />
            <div>
              <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{user.name}</div>
              <div style={{ color: C.silver, fontSize: 11 }}>{timeAgo(p.time, lang)}</div>
            </div>
          </div>
          {p.img && (
            <img src={p.img} alt="" onClick={() => setLightboxSrc(p.img)}
              style={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 12, marginBottom: 8, border: `1px solid ${_T.cardBorderBot}`, cursor: 'pointer' }} />
          )}
          {p.text && <div style={{ color: C.silver2, fontSize: 13, lineHeight: 1.75 }}>{p.text}</div>}
        </div>
      ))}
    </>
  );

  const statsTab = (
    <>
      <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 10 }}>
        {nl ? 'BADGES' : 'BADGES'}
      </div>
      <ProfileBadges userId={user.id} lang={lang} isOwnProfile={isMe} />

      <CompetitionHonours userId={user.id} lang={lang} />

      <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 8, marginTop: 4 }}>
        {nl ? 'SOCIALE VAARDIGHEDEN' : 'SOCIAL SKILLS'}
      </div>
      <SocialStatsCard userId={user.id} lang={lang} allUsers={allUsers || []} friends={friends || []} />
    </>
  );

  return (
    <div style={{ minHeight: '100vh', background: _T.bg, display: 'flex', flexDirection: 'column' }}>
      <style>{CSS}</style>

      {/* ── Hero Banner ── */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <div style={{ height: 200, overflow: 'hidden', position: 'relative' }}>
          {bannerImg && <img src={bannerImg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
          <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(160deg, ${user.bannerColor || _T.bg2}${bannerImg ? 'bb' : 'ff'} 0%, ${accentCol}22 100%)` }} />
          <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 75% 40%, ${accentCol}30, transparent 60%)` }} />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 80, background: `linear-gradient(to bottom, transparent, ${_T.bg})` }} />

          <button onClick={onBack} style={{ position: 'absolute', top: 14, left: 14, background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.1)', color: C.silver2, borderRadius: 10, padding: '7px 13px', cursor: 'pointer', fontSize: 13, backdropFilter: 'blur(8px)', zIndex: 10 }}>
            ← {nl ? 'Terug' : 'Back'}
          </button>

          {isResting && (
            <div style={{ position: 'absolute', top: 14, right: 14, background: 'rgba(42,158,130,0.25)', border: '1px solid #2A9E8266', borderRadius: 20, padding: '5px 12px', backdropFilter: 'blur(8px)' }}>
              <span style={{ fontSize: 12, color: '#2A9E82', fontWeight: 700 }}>🌙 {nl ? 'Aan het rusten' : 'Resting'}</span>
            </div>
          )}
        </div>

        {/* Avatar overlaps banner bottom */}
        <div style={{ position: 'absolute', bottom: -44, left: 20, zIndex: 10, border: `4px solid ${_T.bg}`, borderRadius: '50%', boxShadow: `0 0 0 2px ${accentCol}44` }}>
          <Avatar user={user} size={84} profileImage={user.picture || null} />
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '54px 16px 32px' }}>

        {/* Name row + action buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: C.white, lineHeight: 1.2 }}>{user.name}</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 5, flexWrap: 'wrap' }}>
              {isFriend && !isMe && (
                <span style={{ fontSize: 10, color: accentCol, background: `${accentCol}18`, border: `1px solid ${accentCol}33`, borderRadius: 20, padding: '2px 8px', fontWeight: 700 }}>
                  👤 {nl ? 'Vriend' : 'Friend'}
                </span>
              )}
              {isResting && (
                <span style={{ fontSize: 10, color: '#2A9E82', background: '#2A9E8218', border: '1px solid #2A9E8244', borderRadius: 20, padding: '2px 8px', fontWeight: 700 }}>
                  🌙 {nl ? 'Rustmodus' : 'Resting'}
                </span>
              )}
              {moodData && (
                <span style={{ fontSize: 10, color: moodData.color, background: `${moodData.color}18`, border: `1px solid ${moodData.color}33`, borderRadius: 20, padding: '2px 8px', fontWeight: 700 }}>
                  {moodData.emoji} {nl ? moodData.label : moodData.labelEN}
                </span>
              )}
            </div>
          </div>

          {!isMe && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: 4 }}>
              {hasIncoming && (
                <div style={{ display: 'flex', gap: 5 }}>
                  <button onClick={() => onAcceptFriend(user.id)} style={s_PB({ padding: '8px 12px', fontSize: 12, borderRadius: 10 })}>✓</button>
                  <button onClick={() => onDeclineFriend(user.id)} style={{ background: 'transparent', border: `1px solid ${C.red}44`, color: C.red, borderRadius: 10, padding: '8px 12px', cursor: 'pointer', fontSize: 12 }}>✕</button>
                </div>
              )}
              {!hasIncoming && !isFriend && !hasSentRequest && (
                <button onClick={() => onFriendRequest(user.id)} style={s_PB({ padding: '8px 14px', fontSize: 12, borderRadius: 10 })}>👤+ {nl ? 'Vriend' : 'Friend'}</button>
              )}
              {hasSentRequest && (
                <div style={{ fontSize: 12, color: C.silver, padding: '8px 12px', background: _T.bg4, borderRadius: 10 }}>⏳ {nl ? 'Verzonden' : 'Sent'}</div>
              )}
              {isFriend && !isResting && (
                <button onClick={() => onOpenChat(user)} style={s_GB({ padding: '8px 14px', fontSize: 12, borderRadius: 10 })}>💬</button>
              )}
              {isFriend && onPlayTogether && !isResting && (
                <button onClick={() => onPlayTogether(user)} style={{ ...s_PB({ padding: '8px 14px', fontSize: 12, borderRadius: 10 }), background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, color: '#0a1220', fontWeight: 700 }}>
                  🎮 {nl ? 'Samen Spelen' : 'Play Together'}
                </button>
              )}
            </div>
          )}
        </div>

        {tabBar}

        {activeTab === 'about' && aboutTab}
        {activeTab === 'posts' && postsTab}
        {activeTab === 'stats' && statsTab}
      </div>

      <PhotoLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </div>
  );
}

export default ProfilePage;
